import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import TrustBadges from "../components/TrustBadges";

const HERO_BG =
  "https://images.unsplash.com/photo-1764728145918-e67632b3b2df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxkYXJrJTIwdHJvcGljYWwlMjByYWluZm9yZXN0JTIwanVuZ2xlfGVufDB8fHx8MTc3NzI4NTQzOHww&ixlib=rb-4.1.0&q=85";
const LAB_IMG =
  "https://images.pexels.com/photos/8442376/pexels-photo-8442376.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const TESTIMONIALS = [
  { name: "Marcus R.", text: "Absolutely the cleanest green I've tried. No jitters, pure focus.", rating: 5 },
  { name: "Sofia M.", text: "Lab results on request — transparency I didn't expect. Loyal customer now.", rating: 5 },
  { name: "Daniel K.", text: "Red Borneo is my nightly ritual. Sleep has never been better.", rating: 5 },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then((r) => setProducts(r.data));
    api.get("/settings/public")
      .then((r) => setSettings(r.data))
      .catch(() => {});
  }, []);

  return (
    <>
    <Helmet>
      <title>Kratoven | Premium Borneo Kratom</title>
      <meta
        name="description"
        content="Premium Borneo kratom from Kalimantan. Lab-tested, hand-harvested, sealed fresh, and shipped worldwide via DHL Express."
      />
      <link rel="canonical" href="https://kratoven.com/" />
    </Helmet>
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <img
          src={settings?.homepage_banner || HERO_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          fetchpriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 hero-grad" />
        <div className="absolute inset-0 grain pointer-events-none" />

        <div className="container-kv relative z-10 py-24 md:py-32">
          <div className="max-w-3xl kv-fade">
            <div className="overline mb-6">Premium Borneo · Est. 2026</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-none font-light mb-8">
              The soul of <em className="italic text-[#C8A97E]">Borneo</em>,<br />
              pressed into every gram.
            </h1>
            <p className="text-base md:text-lg text-[#A8A5A1] max-w-xl leading-relaxed mb-10">
              Hand-harvested in the ancient Kalimantan rainforests. Micro-milled, lab-tested,
              and sealed in matte-black resealable foil — Kratoven is <em className="italic text-[#C8A97E]">Kratom Heaven</em>, refined.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/shop" className="btn-gold" data-testid="hero-shop-btn">
                Shop Collection <ArrowRight size={16} />
              </Link>
              <Link to="/about" className="btn-ghost" data-testid="hero-story-btn">
                Our Story
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="container-kv py-16 md:py-20" data-testid="trust-section">
        <TrustBadges />
      </section>

      {/* FEATURED */}
      <section className="container-kv py-16 md:py-20" data-testid="featured-section">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <div className="overline mb-3">Signature Collection</div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-light">
              Four strains. One obsession.
            </h2>
          </div>
          <Link to="/shop" className="text-sm text-[#C8A97E] tracking-widest uppercase hover:text-[#D8BD98]" data-testid="view-all-link">
            View All →
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* LAB */}
      <section className="relative overflow-hidden">
        <div className="container-kv py-20 md:py-28 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline mb-4">Lab-tested, every batch</div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-light mb-6">
              Purity you can verify.
            </h2>
            <p className="text-[#A8A5A1] leading-relaxed mb-6">
              Every harvest is independently tested for alkaloid profile, heavy metals, and
              microbial contaminants. We don't hide our results — we lead with them. Full
              Certificates of Analysis are available on request for every SKU.
            </p>
            <ul className="space-y-3 text-sm text-[#A8A5A1]">
              <li className="flex gap-2"><span className="text-[#C8A97E]">◆</span> Independent third-party labs</li>
              <li className="flex gap-2"><span className="text-[#C8A97E]">◆</span> Full mitragynine & 7-OH analysis</li>
              <li className="flex gap-2"><span className="text-[#C8A97E]">◆</span> Heavy metals & microbial screens</li>
              <li className="flex gap-2"><span className="text-[#C8A97E]">◆</span> Certificate of Analysis per batch</li>
            </ul>
          </div>
          <div className="relative aspect-[4/3] overflow-hidden">
            <img src={LAB_IMG} alt="Lab testing" className="w-full h-full object-cover" loading="lazy" decoding="async" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="container-kv py-20 md:py-28" data-testid="testimonials-section">
        <div className="text-center mb-14">
          <div className="overline mb-3">Voices from the field</div>
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight font-light">
            Thousands of rituals. Every morning.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t) => (
            <figure key={t.name} className="card-kv">
              <div className="flex gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <Star key={i} size={14} className="fill-[#C8A97E] text-[#C8A97E]" />
                ))}
              </div>
              <blockquote className="font-serif text-xl leading-snug mb-5 text-[#F4F1ED]">
                "{t.text}"
              </blockquote>
              <figcaption className="text-xs text-[#75726D] tracking-wider uppercase">— {t.name}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-kv pb-24">
        <div className="card-kv text-center py-16 px-6 border-[#C8A97E]/20">
          <div className="overline mb-4">45-Day Guarantee</div>
          <h2 className="font-serif text-3xl sm:text-4xl font-light mb-4">Start your ritual today.</h2>
          <p className="text-[#A8A5A1] mb-8 max-w-lg mx-auto">
            If it's not the finest kratom you've ever tried, we'll refund you. No questions.
          </p>
          <Link to="/shop" className="btn-gold inline-flex" data-testid="cta-shop-btn">
            Shop the Collection <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
    </>
  );
}
